import React, { useEffect, useState } from 'react';
import { useServiceCostStore } from '../../lib/stores/serviceCostStore';
import { useServiceCostTemplateStore } from '../../lib/stores/serviceCostTemplateStore';
import { useServiceStore } from '../../lib/stores/serviceStore';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
// Removed Select import - using HTML select instead
import { Badge } from '../ui/badge';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { ServiceCostForm } from './ServiceCostForm';
import { formatCurrency } from '../../lib/utils';
import type { ServiceCost, ServiceCostsFilter } from '../../lib/types';

interface ServiceCostListProps {
  serviceId?: string;
  dealId?: string;
  showActions?: boolean;
  mode?: 'template' | 'actual';
  hideFilters?: boolean;
}

export function ServiceCostList({ serviceId, dealId, showActions = true, mode = 'template', hideFilters = false }: ServiceCostListProps) {
  // Use the appropriate store based on mode
  const serviceCostStore = useServiceCostStore();
  const serviceCostTemplateStore = useServiceCostTemplateStore();

  // Select store based on mode
  const store = mode === 'template' ? {
    costs: serviceCostTemplateStore.templates,
    categories: serviceCostTemplateStore.costCategories,
    loading: serviceCostTemplateStore.loading,
    error: serviceCostTemplateStore.error,
    fetchCosts: () => serviceId ? serviceCostTemplateStore.fetchTemplates(serviceId) : Promise.resolve(),
    fetchCategories: serviceCostTemplateStore.fetchCostCategories,
    deleteCost: serviceCostTemplateStore.deleteTemplate,
  } : {
    costs: serviceCostStore.costs,
    categories: serviceCostStore.categories,
    loading: serviceCostStore.loading,
    error: serviceCostStore.error,
    fetchCosts: () => {
      if (!serviceId && !dealId) return serviceCostStore.fetchCosts();
      if (serviceId) return serviceCostStore.fetchCostsByService(serviceId);
      if (dealId) return serviceCostStore.fetchCostsByDeal(dealId);
      return Promise.resolve();
    },
    fetchCategories: serviceCostStore.fetchCategories,
    deleteCost: serviceCostStore.deleteCost,
  };

  const {
    filters,
    approveCost,
    setFilters,
    clearError
  } = serviceCostStore; // These are only used for actual costs

  const { services, fetchServices } = useServiceStore();
  const { partners, fetchPartners } = usePartnerStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState<ServiceCost | null>(null);
  const [costToDelete, setCostToDelete] = useState<ServiceCost | null>(null);
  const [costToApprove, setCostToApprove] = useState<ServiceCost | null>(null);

  useEffect(() => {
    store.fetchCategories();
    store.fetchCosts();

    if (services.length === 0) fetchServices();
    if (partners.length === 0) fetchPartners();
  }, [serviceId, dealId, mode]);

  const handleFilterChange = (key: keyof ServiceCostsFilter, value: string) => {
    setFilters({
      ...filters,
      [key]: value || undefined
    });
  };

  const handleEdit = (cost: ServiceCost) => {
    setSelectedCost(cost);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!costToDelete) return;

    try {
      await store.deleteCost(costToDelete.id);
      setCostToDelete(null);
      // Ricarica la lista dei costi dopo l'eliminazione
      await store.fetchCosts();
    } catch (error) {
      console.error('Error deleting cost:', error);
    }
  };

  const handleApprove = async () => {
    if (!costToApprove) return;
    
    const success = await approveCost(costToApprove.id);
    if (success) {
      setCostToApprove(null);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'review_needed': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getCostTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'actual': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'estimated': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'budgeted': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (store.error) {
    return (
      <div className="text-red-600 p-4 bg-red-50 rounded-lg">
        <p>{store.error}</p>
        <Button
          onClick={() => mode === 'actual' ? clearError() : null}
          variant="outline"
          size="sm"
          className="mt-2"
        >
          Chiudi
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compact Filters - Hidden if hideFilters is true */}
      {!hideFilters && (
        <Card className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <Input
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Cerca per nome o descrizione..."
                className="w-full"
              />
            </div>

            <div className="min-w-[150px]">
              <select
                value={filters.cost_category_id || ''}
                onChange={(e) => handleFilterChange('cost_category_id', e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tutte le categorie</option>
                {store.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[120px]">
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tutti gli stati</option>
                {mode === 'template' ? (
                  <>
                    <option value="pending">Abilitato</option>
                    <option value="cancelled">Disabilitato</option>
                  </>
                ) : (
                  <>
                    <option value="pending">In Attesa</option>
                    <option value="approved">Approvato</option>
                    <option value="review_needed">Da Verificare</option>
                  </>
                )}
              </select>
            </div>

            {showActions && (
              <Button
                onClick={() => {
                  setSelectedCost(null);
                  setIsFormOpen(true);
                }}
                className="bg-[#0f6cbd] hover:bg-[#0a5ba8] text-white"
              >
                Aggiungi Costo
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({})}
            >
              Reset
            </Button>
          </div>
        </Card>
      )}

      {/* Cost List */}
      <div className="space-y-4">
        {store.loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Caricamento costi...</p>
          </div>
        ) : store.costs.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">Nessun costo trovato</p>
            {showActions && (
              <Button
                onClick={() => {
                  setSelectedCost(null);
                  setIsFormOpen(true);
                }}
                className="mt-4 bg-[#0f6cbd] hover:bg-[#0a5ba8] text-white"
              >
{mode === 'template' ? 'Aggiungi il primo costo template' : 'Aggiungi il primo costo effettivo'}
              </Button>
            )}
          </Card>
        ) : (
          store.costs.map((cost) => (
            <Card key={cost.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {cost.name}
                      </h3>
                      {cost.affects_commission && mode === 'actual' && (
                        <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs">
                          Impatta Provvigioni
                        </Badge>
                      )}
                    </div>
                    {cost.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {cost.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 min-w-[100px]">
                    <div className="font-medium text-gray-700 dark:text-gray-300">{cost.cost_category?.name || 'Non categorizzato'}</div>
                  </div>
                  
                  <div className="text-sm text-right min-w-[90px]">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(mode === 'template' ? (cost.default_amount || 0) : (cost.estimated_amount || 0))}
                    </div>
                    <div className="text-xs text-gray-500">{new Date(cost.created_at).toLocaleDateString('it-IT')}</div>
                  </div>
                </div>

                {showActions && (
                  <div className="flex gap-1 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(cost)}
                      className="h-8 px-2"
                    >
                      Modifica
                    </Button>
                    
                    {mode === 'actual' && cost.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCostToApprove(cost)}
                        className="h-8 px-2 text-green-600 border-green-600 hover:bg-green-50"
                      >
                        Approva
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCostToDelete(cost)}
                      className="h-8 px-2 text-red-600 border-red-600 hover:bg-red-50"
                    >
                      Elimina
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedCost(null);
        }}
        title={selectedCost ? 'Modifica Costo' : (mode === 'template' ? 'Nuovo Costo Template' : 'Nuovo Costo Effettivo')}
        size="lg"
      >
        <ServiceCostForm
          cost={selectedCost}
          serviceId={serviceId}
          dealId={dealId}
          mode={mode}
          onSave={() => {
            setIsFormOpen(false);
            setSelectedCost(null);
            store.fetchCosts();
          }}
          onCancel={() => {
            setIsFormOpen(false);
            setSelectedCost(null);
          }}
        />
      </ResponsiveModal>

      {/* Approval Confirmation */}
      <ConfirmDialog
        isOpen={!!costToApprove}
        onClose={() => setCostToApprove(null)}
        onCancel={() => setCostToApprove(null)}
        onConfirm={handleApprove}
        title="Conferma Approvazione"
        message={`Sei sicuro di voler approvare il costo "${costToApprove?.name}"?`}
        confirmText="Approva"
        cancelText="Annulla"
        confirmVariant="primary"
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!costToDelete}
        onClose={() => setCostToDelete(null)}
        onCancel={() => setCostToDelete(null)}
        onConfirm={handleDelete}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il costo "${costToDelete?.name}"? Questa azione non può essere annullata.`}
        confirmText="Elimina"
        cancelText="Annulla"
        confirmVariant="danger"
      />
    </div>
  );
}